import * as React from 'react';
import { TextProps } from 'react-native';

import createElement from 'react-native-web/dist/exports/createElement';
import css from 'react-native-web/dist/exports/StyleSheet/css';
import * as forwardedProps from 'react-native-web/dist/modules/forwardedProps';
import useElementLayout from 'react-native-web/dist/modules/useElementLayout';
import useMergeRefs from 'react-native-web/dist/modules/useMergeRefs';
import usePlatformMethods from 'react-native-web/dist/modules/usePlatformMethods';
import useResponderEvents from 'react-native-web/dist/modules/useResponderEvents';
import StyleSheet from 'react-native-web/dist/exports/StyleSheet';
import TextAncestorContext from 'react-native-web/dist/exports/Text/TextAncestorContext';

import { createPickProps } from './helpers/createPickProps';
import { ElementFromTagName } from './helpers/ElementFromTagName';

const forwardPropsList = {
  ...forwardedProps.defaultProps,
  ...forwardedProps.accessibilityProps,
  ...forwardedProps.clickProps,
  ...forwardedProps.focusProps,
  ...forwardedProps.keyboardProps,
  ...forwardedProps.mouseProps,
  ...forwardedProps.touchProps,
  ...forwardedProps.styleProps,
  href: true,
  lang: true,
  pointerEvents: true,
};

type AllowedTagName = 'div' | 'a' | 'label';

type AllowedProps<T extends AllowedTagName> = Omit<
  React.ComponentProps<T>,
  'ref' | 'key' | 'children' | 'id' | keyof TextProps
>;

// Fixed: Re-implemented to support additional props
const pickProps = createPickProps(forwardPropsList);

export default function createTextComponent<
  T extends AllowedTagName,
  PropNames extends keyof AllowedProps<T>
>(params: { tagName: T; additionalProps: { [K in PropNames]: true } }) {
  type Props = {
    [K in PropNames]?: AllowedProps<T>[K];
  } & { children?: React.ReactNode };
  const Text = React.forwardRef<ElementFromTagName<T>, Props & TextProps>(
    (props, forwardedRef) => {
      // NOTE: Everything from here until the end of this function, with the
      // exception of blocks marked as "Fixed", is sourced directly from:
      // https://github.com/necolas/react-native-web/blob/0.17.5/packages/react-native-web/src/exports/Text/index.js
      const {
        dir,
        hrefAttrs,
        numberOfLines,
        onClick,
        onLayout,
        onPress,
        onMoveShouldSetResponder,
        onMoveShouldSetResponderCapture,
        onResponderEnd,
        onResponderGrant,
        onResponderMove,
        onResponderReject,
        onResponderRelease,
        onResponderStart,
        onResponderTerminate,
        onResponderTerminationRequest,
        onScrollShouldSetResponder,
        onScrollShouldSetResponderCapture,
        onSelectionChangeShouldSetResponder,
        onSelectionChangeShouldSetResponderCapture,
        onStartShouldSetResponder,
        onStartShouldSetResponderCapture,
        selectable,
      } = props;

      const hasTextAncestor = React.useContext(TextAncestorContext);
      const hostRef = React.useRef(null);

      const classList = [
        classes.text,
        hasTextAncestor === true && classes.textHasAncestor,
        numberOfLines != null && classes.textMultiLine,
      ];
      const style = [
        props.style,
        numberOfLines != null && { WebkitLineClamp: numberOfLines },
        selectable === true && styles.selectable,
        selectable === false && styles.notSelectable,
        onPress && styles.pressable,
      ];

      useElementLayout(hostRef, onLayout);
      useResponderEvents(hostRef, {
        onMoveShouldSetResponder,
        onMoveShouldSetResponderCapture,
        onResponderEnd,
        onResponderGrant,
        onResponderMove,
        onResponderReject,
        onResponderRelease,
        onResponderStart,
        onResponderTerminate,
        onResponderTerminationRequest,
        onScrollShouldSetResponder,
        onScrollShouldSetResponderCapture,
        onSelectionChangeShouldSetResponder,
        onSelectionChangeShouldSetResponderCapture,
        onStartShouldSetResponder,
        onStartShouldSetResponderCapture,
      });

      const handleClick = React.useCallback(
        (e) => {
          if (onClick != null) {
            onClick(e);
          } else if (onPress != null) {
            e.stopPropagation();
            onPress(e);
          }
        },
        [onClick, onPress],
      );

      // Fixed: Changed the following to support params.tagName
      let fallback = hasTextAncestor ? 'span' : 'div';
      let component = params.tagName === 'div' ? fallback : params.tagName;

      // Fixed: Added second argument
      const supportedProps = pickProps(props, params.additionalProps);

      // Fixed: Added this `if` block to support className
      if (supportedProps.className) {
        classList.push(supportedProps.className as string);
        delete supportedProps.className;
      }

      supportedProps.classList = classList;
      supportedProps.dir = dir;
      // 'auto' by default allows browsers to infer writing direction (root elements only)
      if (!hasTextAncestor) {
        supportedProps.dir = dir != null ? dir : 'auto';
      }

      if (onClick || onPress) {
        supportedProps.onClick = handleClick;
      }

      supportedProps.style = style;
      if (props.href != null) {
        component = 'a';
        if (hrefAttrs != null) {
          const { download, rel, target } = hrefAttrs;
          if (download != null) {
            supportedProps.download = download;
          }
          if (rel != null) {
            supportedProps.rel = rel;
          }
          if (typeof target === 'string') {
            supportedProps.target =
              target.charAt(0) !== '_' ? '_' + target : target;
          }
        }
      }

      const platformMethodsRef = usePlatformMethods(supportedProps);
      const setRef = useMergeRefs(hostRef, platformMethodsRef, forwardedRef);

      supportedProps.ref = setRef;

      const element = createElement(component, supportedProps);

      return hasTextAncestor ? (
        element
      ) : (
        <TextAncestorContext.Provider value={true}>
          {element}
        </TextAncestorContext.Provider>
      );
    },
  );

  Text.displayName = 'Text';

  return Text;
}

const classes = css.create({
  text: {
    border: '0 solid black',
    boxSizing: 'border-box',
    color: 'black',
    display: 'inline',
    // Fixed: The following 4 font declarations were previously a single line
    // (using the `font` shorthand) and included fontFamily. I'm removing
    // fontFamily so that it will inherit from the global CSS.
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontStretch: 'normal',
    lineHeight: 'normal',
    margin: 0,
    padding: 0,
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },
  textHasAncestor: {
    color: 'inherit',
    font: 'inherit',
    whiteSpace: 'inherit',
  },
  // See #13
  textMultiLine: {
    display: '-webkit-box',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
  },
});

const styles = StyleSheet.create({
  notSelectable: {
    userSelect: 'none',
  },
  selectable: {
    userSelect: 'text',
  },
  pressable: {
    cursor: 'pointer',
  },
});
